import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { Flex, Paragraph, Table } from 'components/patterns'
import { themeColor } from 'utils/styles'
import { shrinkWhenDrawerOpen } from 'components/patterns/Drawer/Drawer.style'
import {
  ConflictsBody,
  CenterMiddlePositionWrap,
  ConflictsHeader,
} from 'components/vehicles/Summary/ChecPlusVehicleSummary/AddRemoveVehicleFeatures/AddRemoveVehicleFeatures.style'
import { StyledConflictsResolveView } from 'components/vehicles/Summary/ChecPlusVehicleSummary/AddRemoveVehicleFeatures/FeatureCollections/FeatureCollections.style'

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
  padding: 0 2px;
  overflow-x: hidden;
  gap: 16px;
  background-color: ${themeColor('whitePrimary')};
  ${({ isEditing }) =>
    isEditing &&
    css`
      padding-bottom: 68px;
    `}

  .add-feature-search {
    display: flex;
    justify-content: center;
    width: 100%;

    .ant-table-wrapper {
      width: 100%;
    }

    table {
      table-layout: fixed !important;
    }

    /* Bug 496741 Issue 8: Allow header text to wrap in the search results table */
    .ant-table-thead > tr > th {
      white-space: normal;
      word-wrap: break-word;
    }

    .ant-table-tbody > tr > td {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  > .ant-row-flex,
  > .ant-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;

    > .ant-col {
      padding-left: 0 !important;
      padding-right: 0 !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;
    }

    .paged-table,
    [class*='StyledPagedTable'] {
      width: 100% !important;
    }

    .ant-table-wrapper {
      width: 100%;
    }
  }
`

// Bug 479122 Issue 10: HeaderWrapper shrinks when drawer is open so Duplicate button shifts
// Bug 479697 Issue 12: Use flex layout so buttons sit outside the blue highlight area
export const HeaderWrapper = styled.div`
  border-bottom: thin solid ${themeColor('greyPrimary')};
  padding-bottom: 8px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${(props) =>
    props.hasWarning ? '#FFF9E6' : 'transparent'};
  transition: background-color 0.2s ease;
  ${(props) =>
    shrinkWhenDrawerOpen(props.isDrawerVisible, props.drawerVisibleWidth)}
`

// Warning icon positioned in the upper right corner of the header (PBI 150666)
export const HeaderWarningIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: ${themeColor('warningAccent')};
  border-radius: 50%;
  color: white;
  font-size: 16px;
  font-weight: bold;
  font-family: Arial, sans-serif;
  cursor: default;
`

// Bug 479697 Issue 12: Header gets blue highlight and blue bar, buttons stay outside
// Bug 479122 Issue 10: Use flex:1 + overflow:hidden so Header absorbs drawer shrinkage
// and HeaderActions (Duplicate button) stays in place without shifting
// Bug 486891 Issue 8: Blue highlight moved to inner HeaderContent to hug content
export const Header = styled(Flex)`
  padding: 5px 16px 5px 5px;
  position: relative;
  cursor: pointer;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`

// Bug 486891 Issue 8: Inner wrapper with fit-content width for blue highlight
export const HeaderContent = styled(Flex)`
  width: fit-content;
  max-width: 100%;
  position: relative;
  padding-right: 16px;
  &:hover {
    background-color: ${themeColor('blueLight')};
  }
  ${({ isSelected, theme }) =>
    isSelected &&
    `
      background-color: ${
        theme.color?.blueLight || theme.blueLight || 'rgba(232, 241, 255, 1)'
      };
      &:hover {
        background-color: ${
          theme.color?.blueLight || theme.blueLight || 'rgba(232, 241, 255, 1)'
        };
      }
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 10px;
        background-color: ${
          theme.color?.blueAccent ||
          theme.blueAccent ||
          theme.btnPrimaryHoverBg ||
          'rgba(30, 122, 255, 1)'
        };
      }
    `}
`

// Bug 479697 Issue 12: Action buttons positioned outside the blue highlight area
export const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 5px;
`

export const HeaderTitleRow = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

export const BoldText = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: ${themeColor('blackSecondary')};
`

export const ValueText = styled.span`
  font-size: 14px;
  font-weight: normal;
  color: ${themeColor('blackSecondary')};
`

export const SectionHeading = styled.h2`
  font-size: 20px;
  font-weight: bold;
  line-height: 24px;
  margin: 0;
  color: ${themeColor('blackPrimary')};
`

export const StyledTable = styled(Table)`
  & .ant-table table {
    table-layout: fixed;
  }
  /* Bug 496741 Issue 8: Allow header text to wrap so narrowed columns don't clip headers */
  & .ant-table-thead > tr > th {
    white-space: normal;
    word-wrap: break-word;
  }
  & .ant-table-tbody > tr > td {
    vertical-align: middle;
    padding-top: 4px;
    padding-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &&& .ant-table-tbody > tr.row-marked-for-addition > td:first-of-type {
    border-left: 4px solid ${themeColor('confirmPrimary')};
  }
  &&& .ant-table-tbody > tr.row-marked-for-deletion > td:first-of-type {
    border-left: 4px solid ${themeColor('errorPrimary')};
  }
`

export const ActionBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 68px;
  background-color: ${themeColor('whitePrimary')};
  border-top: 1px solid ${themeColor('grey')};
  display: flex;
  align-items: center;
  justify-content: center;
  /* Bug 496741 Issue 11: Reduced from 16px to 8px to match standard button spacing */
  gap: 8px;
  padding: 16px;
  z-index: 100;
`

export const FeaturesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

/* Remove / Re-add icon in the features table */
export const RemoveIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
`

/* VPDM warning icon — matches the WarningIcon in AddRemoveVehicleFeatures.style.js */
export const VpdmWarningIcon = styled.img`
  width: 20px;
  height: 18px;
`

/* Flex wrapper for the action column so the remove/add icon and VPDM badge
   sit side-by-side with an 8px gap, matching StyledCurrentLimitWrapper in
   the vehicle feature addition flow. */
export const ActionIconWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`

export const StateIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  ${({ type, theme }) =>
    type === 'addition' &&
    `background: ${
      theme.confirmPrimary ||
      theme.color?.confirmPrimary ||
      'rgba(57, 135, 18, 1)'
    };`}
  ${({ type, theme }) =>
    type === 'deletion' &&
    `background: ${
      theme.errorPrimary || theme.color?.errorPrimary || 'rgba(180, 4, 12, 1)'
    };`}
`

/* Commit View Styles */
export const CommitViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`

export const CommitViewHeading = styled.h2`
  font-size: 20px;
  font-weight: bold;
  line-height: 24px;
  margin: 0;
  color: ${themeColor('blackPrimary')};
`

export const CommitViewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const CommitViewSectionHeading = styled.h3`
  font-size: 16px;
  font-weight: bold;
  line-height: 22px;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

export const CommitViewFeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 3px;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColor('blackSecondary')};
  ${({ type }) =>
    type === 'add' &&
    `
      background-color: rgba(57, 135, 18, 0.08);
      border-left: 4px solid rgba(57, 135, 18, 1);
    `}
  ${({ type }) =>
    type === 'remove' &&
    `
      background-color: rgba(180, 4, 12, 0.08);
      border-left: 4px solid rgba(180, 4, 12, 1);
    `}
`

export const CommitViewFeaturePartNumber = styled.span`
  font-weight: bold;
  min-width: 140px;
`

export const CommitViewButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${themeColor('grey')};
`

// Table Section Header with optional warning icon (PBI 150666)
export const TableSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`

export const TableSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin: 0;
  color: ${themeColor('blackPrimary')};
`

export const WarningIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: ${themeColor('warningAccent')};
  border-radius: 50%;
  color: white;
  font-size: 14px;
  font-weight: bold;
  font-family: Arial, sans-serif;
`

/* Drawer Styles */
export const DrawerContentWrapper = styled.div`
  margin: -20px;
  height: calc(100% + 32px);
  width: calc(100% + 40px);
  display: flex;
  padding: 0 20px;
  gap: 16px;
  flex-direction: column;
`

export const DrawerHeaderWrapper = styled.div`
  padding: 20px 0 16px;
  border-bottom: thin solid ${themeColor('greyPrimary')};
`

export const DrawerTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: ${themeColor('blackPrimary')};
`

export const BoldDrawerContentText = styled(Paragraph)`
  font-size: 12px;
  font-weight: bold;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

export const DescriptionParagraph = styled(Paragraph)`
  font-size: 12px;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

// Drawer Section with bottom border (PBI 150666)
export const DrawerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: thin solid ${themeColor('greyPrimary')};
`

export const DrawerSectionLabel = styled.p`
  font-size: 12px;
  font-weight: bold;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

export const DrawerSectionValue = styled.p`
  font-size: 12px;
  font-weight: normal;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

/* Edit mode drawer styles */
export const DrawerEditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

export const DrawerEditLabel = styled.label`
  font-size: 12px;
  color: ${themeColor('blackSecondary')};
`

export const DrawerEditInput = styled.input`
  width: 100%;
  padding: 7px 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColor('blackPrimary')};
  background-color: ${themeColor('whitePrimary')};
  border: 1px solid ${themeColor('slateLight')};
  border-radius: 3px;
  font-family: Arial, sans-serif;

  &:focus {
    outline: none;
    border-color: ${themeColor('blueAccent')};
  }
`

export const DrawerEditTextarea = styled.textarea`
  width: 100%;
  padding: 6px 8px;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColor('blackSecondary')};
  background-color: ${themeColor('whitePrimary')};
  border: 1px solid ${themeColor('slateLight')};
  border-radius: 3px;
  font-family: Arial, sans-serif;
  height: 75px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${themeColor('blueAccent')};
  }
`

export const DrawerCharCount = styled.p`
  font-size: 10px;
  color: ${themeColor('blackSecondary')};
  margin: 0;
  text-align: right;
  letter-spacing: 0.2px;
  line-height: 14px;
`

// Drawer Metadata styles (PBI 150666)
export const DrawerMetaRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

export const DrawerMetaLabel = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: ${themeColor('blackSecondary')};
`

export const DrawerMetaValue = styled.span`
  font-size: 12px;
  color: ${themeColor('blackSecondary')};
`

// Title Field styles for drawer (PBI 150666 - Invalid Activation, Missing Title)
export const TitleFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const TitleFieldLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: ${themeColor('blackSecondary')};

  &::before {
    content: '* ';
    color: ${themeColor('errorAccent')};
  }
`

export const TitleFieldInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  font-family: Arial, sans-serif;
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError
        ? theme.errorAccent || '#F56C6C'
        : theme.greyPrimary || '#d9d9d9'};
  border-radius: 4px;
  outline: none;
  color: ${themeColor('blackPrimary')};

  &::placeholder {
    color: ${themeColor('greySecondary')};
  }

  &:focus {
    border-color: ${({ hasError, theme }) =>
      hasError
        ? theme.errorAccent || '#F56C6C'
        : theme.btnPrimaryFocusColor || '#40a9ff'};
  }
`

export const RequiredText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${themeColor('errorAccent')};
`

// Confirmation Overlay Container (PBI 150666)
// Styles for the Activate/Obsolete confirmation panel
export const ConfirmationOverlayContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: -20px;
  padding: 20px;
  width: calc(100% + 40px);

  & .confirm-obsolete-overlay {
    flex: 1;

    & h3 {
      font-size: 16px;
      margin-bottom: 8px;
      color: ${themeColor('blackSecondary')};
    }

    & p {
      font-weight: normal;
      font-size: 14px;
    }
  }

  & .confirm-obsolete-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 16px;
    border-top: thin solid ${themeColor('greyPrimary')};
    margin-top: auto;
  }
`

/* Bug 485870: Empty state when collection has no features */
export const EmptyFeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 45px 0;
  border-radius: 3px;
  border: 1px solid ${themeColor('grey')};
  background-color: ${themeColor('greySecondary')};
`

export const EmptyFeaturesText = styled(Paragraph)`
  font-size: 14px;
  line-height: 18px;
  color: ${themeColor('greyPrimary')};
  margin: 0;
`

/* WP-3 3.2: Wrapper for the "Only Show Compatible Features" checkbox */
export const CompatibleCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
`

/* Hide the AdvancedSearch's internal "Only Show Compatible Features" checkbox
   and replace with our own that does client-side filtering via a MutationObserver */
/* Bug 499041 Issue 25: Fix alignment of search area and search results table.
   - Remove justify-content override so SearchFormRow keeps its natural center
     alignment; PageWrapper's overflow-x:hidden handles left-side clipping.
   - Neutralise Ant Design Row negative margin (-6px) that shifts the results
     heading and table outside the container.
   - Force ant-col-24 to full width (mirrors PageWrapper's rules for its direct
     ant-row-flex children) so the search results table matches the Features table. */
export const SearchWrapper = styled.div`
  .ant-row-flex,
  .ant-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;

    > .ant-col {
      padding-left: 0 !important;
      padding-right: 0 !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;
    }

    .paged-table,
    [class*='StyledPagedTable'] {
      width: 100% !important;
    }

    .ant-table-wrapper {
      width: 100%;
    }
  }
`

/* WP-3 3.4: ECU resource limit warning banner */
export const EcuWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: 2px solid ${themeColor('warningAccent')};
  border-radius: 4px;
  background-color: #fffbe6;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColor('blackSecondary')};
`

/* WP-3 3.7: Unsaved changes indicator in the action bar */
export const UnsavedChangesIndicator = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${themeColor('blackSecondary')};
  white-space: nowrap;
`

/* WP-3 3.3: Incompatible feature icon wrapper in search results */
export const IncompatibleIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
`

/* PBI 144828: Back-navigation breadcrumb on the Feature Collection Detail page */
export const BreadcrumbLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${themeColor('bluePrimary')};
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  &:hover {
    text-decoration: underline;
  }
`

/**
 * Full-viewport overlay for the CollectionCommitView.
 * Covers the entire viewport including the nav header and action bar footer,
 * so the commit screen is fully focused with no underlying UI visible.
 */
export const CommitViewOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: ${themeColor('whitePrimary')};
  display: flex;
  flex-direction: column;
`

/* Conflict resolution action bar — mirrors CommitActionBar in CollectionCommitView.
   Sits at the bottom of the CommitViewOverlay flex column. */
export const ConflictActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  height: 68px;
  border-top: 1px solid ${themeColor('grey')};
  background-color: ${themeColor('whitePrimary')};
  flex-shrink: 0;
`

/* Scrollable content area for conflict resolution views — fills remaining
   space above ConflictActionBar in the CommitViewOverlay flex column.

   Overview layout matches Figma 5202:113440:
   flex-col, gap 45px, items-center, padding 16px 18px, no background.
   The page background (white) shows through.

   ConflictsBody grey bg is cleared — the page bg is the correct background.
   CenterMiddlePositionWrap centering is removed (card sits at top, not middle).

   hideInternalHeader: table view passes this to suppress the ConflictsResolveView's
   own ConflictsHeader — we render ConflictSubHeader above the content area instead. */
export const ConflictContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: ${themeColor('modalGrey')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 45px;
  padding: 16px 18px;
  ${ConflictsBody} {
    height: 100%;
    background-color: transparent;
    padding: 0;
  }
  ${CenterMiddlePositionWrap} {
    height: auto;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
  }
  ${StyledConflictsResolveView} {
    height: 100%;
    width: 100%;
  }
  ${({ hideInternalHeader }) =>
    hideInternalHeader &&
    css`
      ${ConflictsHeader} {
        display: none;
      }
    `}
`

/* Full-height page wrapper for the conflict resolution overlay — matches
   CommitViewPage in CollectionCommitView (flex column, fills overlay). */
export const ConflictViewPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: ${themeColor('whitePrimary')};
`

/* Figma 5977:173726 — table-view sub-header: bold title left, status text right.
   White bg, border-bottom, height 82px. */
export const ConflictSubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 82px;
  padding: 25px 18px;
  border-bottom: 1px solid ${themeColor('grey')};
  background-color: ${themeColor('whitePrimary')};
  flex-shrink: 0;
`

export const ConflictSubHeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  line-height: 32px;
  margin: 0;
  color: ${themeColor('blackPrimary')};
  font-family: Arial, sans-serif;
`

export const ConflictSubHeaderStatus = styled.p`
  font-size: 14px;
  line-height: 18px;
  margin: 0;
  color: ${themeColor('blackSecondary')};
  font-family: Arial, sans-serif;
`

/* Figma 5202:113440 — overview back-link row: chevron + "Back to Feature Collection".
   12px, #0051C4 (linkColor), matches the global PageMetaLink pattern.
   width: 100% so it spans the full content area width. */
export const ConflictBackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${themeColor('linkColor')};
  font-size: 12px;
  font-family: Arial, sans-serif;
  line-height: 16px;
  width: 100%;
  &:hover {
    text-decoration: underline;
  }
`
